import React from 'react';
import { Zap, BookOpen, PlayCircle, Sparkles, Wrench } from 'lucide-react';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@cotera/client/v0/components/ui/card';
import { Icon } from '@cotera/client/app/components/ui/icon';
import { useAction } from '../actions/framework';
import { NewAgentAction } from '../agent/new-agent.action';
import { ShimmerText } from '../components/ui/shimmer-text';

interface ResourceLinkProps {
  href: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const ResourceLink: React.FC<ResourceLinkProps> = ({
  href,
  icon,
  title,
  description,
}) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-4 p-4 rounded-lg hover:bg-zinc-50 transition-colors group border border-muted-foreground-200"
    >
      <div className="w-10 h-10 bg-muted-foreground-100 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-muted-foreground-200">
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-muted-foreground-900 mb-1 flex items-center gap-2">
          {title}
          <Icon
            icon="arrow-right"
            className="text-muted-foreground-400 group-hover:text-muted-foreground-600"
          />
        </h3>
        <p className="text-sm text-muted-foreground-600">{description}</p>
      </div>
    </a>
  );
};

export const HomePageFallbackNewUser: React.FC = () => {
  const newAgentAction = useAction(NewAgentAction);
  return (
    <div className="min-h-full w-full overflow-auto">
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-muted-foreground-900 mb-4">
            Welcome to Cotera!
          </h1>
          <p className="text-xl text-muted-foreground-600 mb-12">
            AI agents that act on your data
          </p>

          {/* Primary CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <Button
              size="lg"
              className="bg-blue-50 border border-blue-200 hover:bg-blue-100 active:bg-blue-100 text-blue-600"
              onClick={() => newAgentAction.execute()}
            >
              <Sparkles className="w-5 h-5" />
              <ShimmerText>Create your first agent</ShimmerText>
            </Button>
          </div>
        </div>

        {/* Learning Resources */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Getting started</CardTitle>
            <CardDescription>
              Learn how to build AI agents and automate your workflows
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <ResourceLink
                href="https://cotera.co/docs/learn/basic-tutorials/create-your-first-agent"
                icon={
                  <PlayCircle className="w-5 h-5 text-muted-foreground-600" />
                }
                title="Quick start guide"
                description="Build your first agent in minutes"
              />
              <ResourceLink
                href="https://cotera.co/prompts"
                icon={<Zap className="w-5 h-5 text-muted-foreground-600" />}
                title="Example workflows"
                description="See real-world agent automation"
              />
              <ResourceLink
                href="https://cotera.co/docs/reference/tools/individual-tools"
                icon={<Wrench className="w-5 h-5 text-muted-foreground-600" />}
                title="Tools"
                description="Connect to Slack, BigQuery, and more"
              />
              <ResourceLink
                href="https://cotera.co/docs"
                icon={
                  <BookOpen className="w-5 h-5 text-muted-foreground-600" />
                }
                title="Documentation"
                description="Complete guide to Cotera"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
